import type { OrderFormInfoData, PhysicianInfo } from '@/types/RequisitionFormTypes'

/**
 * Current user's own record from GET /auth/me/profile. ``firstName`` / ``lastName`` are the
 * practitioner's stored name parts; they are authoritative over any split of the Auth0 display
 * name, which cannot tell a middle name from a surname (GEN-3940).
 */
export type SelfProfile = {
  phone: string
  roleTitle: string
  firstName: string
  lastName: string
}

/**
 * Applies the resolved self-profile phone / roleTitle to the submitter's provider blocks when
 * the logged-in user is the ordering provider and those fields are otherwise blank. Never
 * overwrites a non-empty value (e.g. a FHIR/DB-sourced phone). Shared by the submit path
 * and the server-side TRF preview so both persist the same provider contact info.
 *
 * ``preferProfileNameParts`` also replaces the name with the stored parts. Unlike phone and
 * roleTitle this overwrites a non-empty value, because the value it replaces is a guess:
 * splitting a display name keeps only the first token as the given name and folds any middle
 * name into the surname (GEN-3940). It is applied here rather than left to the component's
 * ordering-provider memo so a submit that resolves the profile mid-flight still sends the
 * stored parts -- that memo was computed in an earlier render and cannot see them.
 *
 * On a self-submit `patientPhysician` and `authorizingProvider` are copies of the ordering
 * provider, so they are corrected too: leaving them on the display-name split would both put
 * the wrong name on the order and make provider-api read the ordering/authorizing name
 * mismatch as an on-behalf submit. They are matched by id, so a *different* clinician's block
 * (which carries their own practitioner id) is never touched.
 */
export function applySelfProviderContact(
  info: OrderFormInfoData | null,
  profile: { phone: string; roleTitle: string; firstName?: string; lastName?: string } | null,
  currentUserId: string | null,
  preferProfileNameParts = false
): OrderFormInfoData | null {
  if (!info?.orderingProvider) return info
  if (!profile) return info
  // Only backfill the submitter's own record, not a different clinician's.
  if (currentUserId != null && info.orderingProvider.id !== currentUserId) return info
  const storedFirst = profile.firstName?.trim() ?? ''
  const storedLast = profile.lastName?.trim() ?? ''
  const useStoredParts = preferProfileNameParts && Boolean(storedFirst || storedLast)

  /** Returns the block unchanged when there is nothing to backfill, so callers can compare by identity. */
  const withSelfContact = (block: PhysicianInfo): PhysicianInfo => {
    const phone = block.phone?.trim() || profile.phone
    const roleTitle = block.roleTitle?.trim() || profile.roleTitle
    // Each part falls back independently so a practitioner with only one part recorded keeps
    // the other from the split.
    const nextName = useStoredParts
      ? {
          firstName: storedFirst || block.name?.firstName || '',
          lastName: storedLast || block.name?.lastName || '',
        }
      : null
    const nameChanged =
      nextName != null &&
      (nextName.firstName !== (block.name?.firstName ?? '') || nextName.lastName !== (block.name?.lastName ?? ''))
    if (!nameChanged && phone === (block.phone ?? '') && roleTitle === (block.roleTitle ?? '')) {
      return block
    }
    return { ...block, phone, roleTitle, ...(nameChanged && nextName != null && { name: nextName }) }
  }

  const selfId = info.orderingProvider.id
  const isSubmitter = (block: PhysicianInfo | undefined): block is PhysicianInfo =>
    block != null && Boolean(selfId) && block.id === selfId

  const orderingProvider = withSelfContact(info.orderingProvider)
  const patientPhysician = isSubmitter(info.patientPhysician)
    ? withSelfContact(info.patientPhysician)
    : info.patientPhysician
  const authorizingProvider = isSubmitter(info.authorizingProvider)
    ? withSelfContact(info.authorizingProvider)
    : info.authorizingProvider

  if (
    orderingProvider === info.orderingProvider &&
    patientPhysician === info.patientPhysician &&
    authorizingProvider === info.authorizingProvider
  ) {
    return info
  }
  return {
    ...info,
    orderingProvider,
    ...(patientPhysician != null && { patientPhysician }),
    ...(authorizingProvider != null && { authorizingProvider }),
  }
}
